import { get } from 'lodash';
import { arrayToObject, ColumnType } from '@treelab/panda';
import { BooleanFilter } from '@treelab/common-logic';

import { IBatchedGetTableData } from './network';
import { IViewOption } from '../../types/core';
import { IViewResponse, IReduxUser } from './types';
import {
  GetTableData_getTableData_rows,
  GetTableData_getTableData_views,
  GetTableData_getTableData,
  GetTableData_getTableData_columns,
} from '../../../graphql/core/types/GetTableData';
import { store } from '../stores';
import {
  GetWorkspaceQuery_workspace,
  GetWorkspaceQuery_workspace_nodes,
} from '../../../graphql/core/types/GetWorkspaceQuery';
import { ViewType } from './graphql/types';
import { isNotNull } from '../../types/util';
import { EXCLUDED_VIEW_TYPES } from '../../constants/core';

/** Hydrate all the records with the old way of doing fields & rows */
const getRecordById = (records: GetTableData_getTableData_rows[]) =>
  arrayToObject(records, 'id') as {
    [key: string]: GetTableData_getTableData_rows;
  };

const getFieldById = (fields: GetTableData_getTableData['columns']) =>
  arrayToObject(fields!, 'id') as {
    [key: string]: GetTableData_getTableData_columns;
  };

const getViewById = (
  fields: GetTableData_getTableData_views['columns'],
  records: GetTableData_getTableData_views['rows'],
  viewId: string,
) => ({
  [viewId]: {
    id: viewId,
    fieldAllIds: fields.map((field) => field!.id),
    recordsAllIds: records.map((record) => record!.id),
  },
});

const getViewsByIdsAndFilterAndSort = (
  views: GetTableData_getTableData_views[],
  mainTable: GetTableData_getTableData,
  fieldsById: {
    [key: string]: GetTableData_getTableData_columns;
  },
) => {
  const booleanFilter = new BooleanFilter();
  const { core } = store.getState();
  const taskflowViewAllId = core.visibleNodes[mainTable.id].taskflow?.views.all;
  const accum: IViewResponse['viewById'] = {};

  return views.reduce((accum, view) => {
    if (EXCLUDED_VIEW_TYPES[view.type]) return accum;

    const globalFilters = _getFilters(get(view, 'controls.globalFilters') as any);
    const localFilters = _getFilters(get(view, 'controls.localFilters') as any);

    booleanFilter.setModel(
      globalFilters.concat(localFilters),
      fieldsById,
      core.me.id,
      undefined,
      false,
    );
    const recordsAllIds = filterAndSortRecords(
      mainTable.rows,
      view.rows,
      booleanFilter,
      view.id,
      taskflowViewAllId,
    );

    return {
      ...accum,
      [view.id]: {
        id: view.id,
        // When we need to hide these columns by view.
        // fieldAllIds: view.columns.filter((column) => !column!.isHidden).map((column) => column!.id),
        fieldAllIds: view.columns.filter((column) => !column!.isHidden).map((column) => column!.id),
        // @ts-ignore
        recordsAllIds,
      },
    };
  }, accum);
};

const filterAndSortRecords = (
  rows: GetTableData_getTableData['rows'],
  order: GetTableData_getTableData_views['rows'],
  filter: BooleanFilter,
  viewId: string,
  taskflowViewAllId?: string,
): string[] => {
  if (!rows) return [];
  const filteredIds: { [id: string]: string } = {};

  rows.forEach((row) => {
    if (viewId === taskflowViewAllId) {
      filteredIds[row.id] = row.id;
    } else {
      if (filter.doesFilterPass({ data: row.cells })) {
        filteredIds[row.id] = row.id;
      }
    }
  });

  return order.reduce((accum, row) => {
    if (row && filteredIds[row.id]) {
      return accum.concat([row.id]);
    }
    return accum;
  }, [] as string[]);
};

const adaptFilterByFilterType = (filter: any, filterType: ColumnType) => {
  switch (filterType) {
    // For these filter types, the filter should be an array of IDs
    case ColumnType.STATUS:
    case ColumnType.SELECT:
    case ColumnType.MULTI_SELECT:
    case ColumnType.COLLABORATOR:
    case ColumnType.CREATED_BY:
      if (Array.isArray(filter)) {
        // In case filter array is read-only
        return [...filter];
      }

      if (typeof filter === 'string') {
        try {
          // Filter will be a stringified array most of the time
          return JSON.parse(filter);
        } catch {
          // If not stringified array, must be an ID
          return [filter];
        }
      }

      return [];
    default:
      return filter;
  }
};

const _getFilters = (filters: any) => {
  if (!filters.filterValues) return [];

  return filters.filterValues.map((val: any) => ({
    ...val,
    filterString: adaptFilterByFilterType(val.filterString, val.filterType),
    operatorType: filters.operatorType,
  }));
};

const getTableById = (table: GetTableData_getTableData) => ({
  [table.id]: {
    id: table.id,
    defaultViewId: table.views![0]!.id,
  },
});

const adaptFieldData = (
  fields: GetTableData_getTableData['columns'],
): GetTableData_getTableData_columns[] => {
  if (!fields) return [];
  return fields
    .map((field) => {
      if (!field) return null;

      return {
        ...field,
        typeOptions: {
          ...field.typeOptions,
          type: field.type,
        },
      };
    })
    .filter(isNotNull);
};

const adaptInitialTable = (table: GetTableData_getTableData) => {
  const adaptedFields = adaptFieldData(table.columns);
  const fieldById = getFieldById(adaptedFields);
  const records = table.rows!;
  const recordById = getRecordById(records);

  return {
    viewById: getViewsByIdsAndFilterAndSort(
      table.views! as GetTableData_getTableData_views[],
      table,
      fieldById,
    ),
    tableById: getTableById(table),
    recordById: recordById,
    fieldById,
  };
};

const adaptTable = (table: GetTableData_getTableData) => {
  const adaptedFields = adaptFieldData(table.columns);
  const fieldById = getFieldById(adaptedFields);
  const mainView = table.views![0];
  const records = table.rows!;
  const fieldOrders = mainView!.columns;
  const recordOrders = mainView!.rows;

  const recordById = getRecordById(records);

  return {
    viewById: getViewById(fieldOrders, recordOrders, mainView!.id),
    tableById: getTableById(table),
    recordById: recordById,
    fieldById,
  };
};

export const adaptWorkspaceData = (workspace: GetWorkspaceQuery_workspace) => {
  const workspaceNodes = workspace.nodes || [];
  const workspaceUsers = workspace.users || [];

  const visibleNodes: { [id: string]: Omit<GetWorkspaceQuery_workspace_nodes, '__typename'> } =
    workspaceNodes.length ? arrayToObject(workspaceNodes, 'id') : {};

  const users: { [id: string]: IReduxUser } = workspaceUsers.length
    ? arrayToObject(workspaceUsers, 'id')
    : {};

  return { visibleNodes, users };
};

// used to transform graphQL response data to schema used by file creator
// this function is for single table responses
export const adaptTableData = (
  data: GetTableData_getTableData,
  initialAccum: IViewResponse,
  isInit?: boolean,
) => {
  const adapted = isInit ? adaptInitialTable(data) : adaptTable(data);

  return {
    viewById: {
      ...initialAccum.viewById,
      ...adapted.viewById,
    },
    tableById: {
      ...initialAccum.tableById,
      ...adapted.tableById,
    },
    recordById: {
      ...initialAccum.recordById,
      ...adapted.recordById,
    },
    fieldById: {
      ...initialAccum.fieldById,
      ...adapted.fieldById,
    },
  };
};

// used to transform graphQL response data to schema used by file creator
// this function is for batched table responses
export const accumulateTableData = (data: IBatchedGetTableData, initialAccum: IViewResponse) => {
  return Object.values(data).reduce(
    (accum: IViewResponse, tableData: GetTableData_getTableData) => {
      if (!tableData) return accum;
      const adapted = adaptTable(tableData);

      return {
        viewById: {
          ...accum.viewById,
          ...adapted.viewById,
        },
        tableById: {
          ...accum.tableById,
          ...adapted.tableById,
        },
        recordById: {
          ...accum.recordById,
          ...adapted.recordById,
        },
        fieldById: {
          ...accum.fieldById,
          ...adapted.fieldById,
        },
      };
    },
    initialAccum,
  );
};

/** table options helpers */
export const adaptOptions = (tableData: GetTableData_getTableData) => {
  const viewOptionById: { [key: string]: IViewOption } = {};

  if (tableData.views === null) {
    return {
      tableOptionById: {
        [tableData.id]: {
          id: [tableData.id],
          viewOptionsAllIds: [],
        },
      },
      viewOptionById: {},
    };
  }

  const viewOptionsAllIds = tableData.views
    .map((view) => {
      if (!view || view.type === ViewType.FORM) return null;
      viewOptionById[view.id] = view;
      return view.id;
    })
    .filter(isNotNull);

  const tableOptionById = {
    [tableData.id]: {
      id: [tableData.id],
      viewOptionsAllIds,
    },
  };

  return {
    tableOptionById: tableOptionById,
    viewOptionById: viewOptionById,
  };
};
